import { z } from 'zod';
import { LedgerVatLineSchema } from './accounting';
// ---------- B2 Libros del autónomo: gastos, bienes de inversión y borradores 303/130 ----------
//
// Solo para emisores con régimen `IRPF_BOOKS` (persona física que lleva los libros del art. 68
// RIRPF). Toga NO presenta nada: los borradores de los modelos 303 y 130 son orientativos y los
// revisa y presenta la gestoría. No incluyen prorrata, regímenes especiales, deducciones
// específicas ni compensaciones de ejercicios anteriores.
export const ExpenseKindSchema = z.enum(['GASTO', 'INVERSION']);
export const EXPENSE_KIND_LABEL = {
    GASTO: 'Gasto',
    INVERSION: 'Bien de inversión',
};
export const ExpenseCategorySchema = z.enum([
    'ALQUILER',
    'SUMINISTROS',
    'SERVICIOS_PROFESIONALES',
    'SEGUROS',
    'CUOTA_AUTONOMOS',
    'CUOTA_COLEGIAL',
    'PERSONAL',
    'FORMACION',
    'MATERIAL_OFICINA',
    'SOFTWARE_Y_TELECOM',
    'VEHICULO_Y_DESPLAZAMIENTOS',
    'PUBLICIDAD',
    'TRIBUTOS',
    'GASTOS_FINANCIEROS',
    'EQUIPOS_Y_MOBILIARIO',
    'OTROS',
]);
export const EXPENSE_CATEGORY_LABEL = {
    ALQUILER: 'Alquiler del despacho',
    SUMINISTROS: 'Suministros (luz, agua, internet…)',
    SERVICIOS_PROFESIONALES: 'Servicios profesionales (gestoría, procuraduría…)',
    SEGUROS: 'Seguros (RC profesional…)',
    CUOTA_AUTONOMOS: 'Cuota de autónomos (Seguridad Social)',
    CUOTA_COLEGIAL: 'Cuota colegial y mutualidad',
    PERSONAL: 'Personal (sueldos y Seguridad Social)',
    FORMACION: 'Formación y bibliografía',
    MATERIAL_OFICINA: 'Material de oficina',
    SOFTWARE_Y_TELECOM: 'Software y telecomunicaciones',
    VEHICULO_Y_DESPLAZAMIENTOS: 'Vehículo y desplazamientos',
    PUBLICIDAD: 'Publicidad y representación',
    TRIBUTOS: 'Tributos y tasas',
    GASTOS_FINANCIEROS: 'Gastos financieros y comisiones',
    EQUIPOS_Y_MOBILIARIO: 'Equipos y mobiliario',
    OTROS: 'Otros gastos',
};
/** Categorías que normalmente no llevan IVA: la pantalla propone el 0 %. */
export const EXPENSE_CATEGORIES_WITHOUT_VAT = [
    'CUOTA_AUTONOMOS',
    'CUOTA_COLEGIAL',
    'PERSONAL',
    'TRIBUTOS',
    'GASTOS_FINANCIEROS',
];
/** Tipos de IVA habituales en una factura recibida. */
export const EXPENSE_VAT_RATES = ['0.2100', '0.1000', '0.0400', '0.0000'];
const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha con formato AAAA-MM-DD');
const cents = z.number().int().min(0).max(1_000_000_000);
const pct = z.number().int().min(0).max(100);
const rate = z
    .union([z.string(), z.number()])
    .transform((v) => Number(v))
    .refine((v) => Number.isFinite(v) && v >= 0 && v <= 0.3, 'Tipo de IVA entre 0 y 30 %');
const ExpenseFieldsSchema = z.object({
    kind: ExpenseKindSchema.default('GASTO'),
    category: ExpenseCategorySchema,
    issueDate: ymd,
    supplierName: z.string().trim().min(1, 'Indica el proveedor').max(200),
    supplierNif: z.string().trim().max(30).nullable().optional(),
    invoiceNumber: z.string().trim().max(60).nullable().optional(),
    description: z.string().trim().max(500).nullable().optional(),
    baseCents: cents,
    vatRate: rate.default(0),
    /** Si se omite, se calcula como base × tipo. */
    vatCents: cents.optional(),
    deductiblePct: pct.default(100),
    vatDeductiblePct: pct.default(100),
    usefulLifeYears: z.number().int().min(1).max(50).nullable().optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
});
export const CreateExpenseSchema = ExpenseFieldsSchema.extend({
    issuerId: z.string().min(1),
}).superRefine((v, ctx) => {
    if (v.kind === 'INVERSION' && !v.usefulLifeYears) {
        ctx.addIssue({
            code: 'custom',
            path: ['usefulLifeYears'],
            message: 'Un bien de inversión necesita su vida útil en años',
        });
    }
    if (v.kind === 'GASTO' && v.usefulLifeYears) {
        ctx.addIssue({
            code: 'custom',
            path: ['usefulLifeYears'],
            message: 'Solo un bien de inversión lleva vida útil',
        });
    }
});
/** Edición parcial: la coherencia (vida útil, IVA) se revalida en el servidor con lo guardado. */
export const UpdateExpenseSchema = ExpenseFieldsSchema.partial();
export const ExpenseDtoSchema = z.object({
    id: z.string(),
    issuerId: z.string(),
    issuerName: z.string(),
    kind: ExpenseKindSchema,
    category: ExpenseCategorySchema,
    issueDate: z.string(),
    supplierName: z.string(),
    supplierNif: z.string().nullable(),
    invoiceNumber: z.string().nullable(),
    description: z.string().nullable(),
    baseCents: z.number().int(),
    /** Tipo de IVA como fracción («0.2100»). */
    vatRate: z.string(),
    vatCents: z.number().int(),
    totalCents: z.number().int(),
    deductiblePct: z.number().int(),
    vatDeductiblePct: z.number().int(),
    /** Base × % deducible en el IRPF; 0 en un bien de inversión (se deduce su amortización). */
    deductibleBaseCents: z.number().int(),
    /** Cuota de IVA × % deducible. */
    deductibleVatCents: z.number().int(),
    usefulLifeYears: z.number().int().nullable(),
    notes: z.string().nullable(),
    hasAttachment: z.boolean(),
    attachmentName: z.string().nullable(),
    attachmentSize: z.number().int().nullable(),
    createdAt: z.string(),
});
export const ExpenseListQuerySchema = z.object({
    issuerId: z.string().min(1).optional(),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    quarter: z.coerce.number().int().min(1).max(4).optional(),
    month: z.coerce.number().int().min(1).max(12).optional(),
    kind: ExpenseKindSchema.optional(),
    category: ExpenseCategorySchema.optional(),
    /** Texto libre: proveedor, NIF, nº de factura o descripción. */
    q: z.string().trim().max(100).optional(),
});
export const ExpenseTotalsSchema = z.object({
    count: z.number().int(),
    baseCents: z.number().int(),
    vatCents: z.number().int(),
    totalCents: z.number().int(),
    deductibleBaseCents: z.number().int(),
    deductibleVatCents: z.number().int(),
});
export const ExpenseListResponseSchema = z.object({
    rows: z.array(ExpenseDtoSchema),
    totals: ExpenseTotalsSchema,
    truncated: z.boolean(),
});
// ---------- Lectura de una factura recibida con IA ----------
export const ExpenseSuggestionSchema = z.object({
    supplierName: z.string().nullable(),
    supplierNif: z.string().nullable(),
    invoiceNumber: z.string().nullable(),
    issueDate: ymd.nullable(),
    baseCents: z.number().int().nullable(),
    vatRate: z.string().nullable(),
    vatCents: z.number().int().nullable(),
    totalCents: z.number().int().nullable(),
    category: ExpenseCategorySchema.nullable(),
    description: z.string().nullable(),
});
export const ExpenseExtractResponseSchema = z.object({
    suggestion: ExpenseSuggestionSchema,
    /** Cosas a revisar a mano: importes que no cuadran, NIF dudoso, campos no leídos… */
    warnings: z.array(z.string()),
});
// ---------- Libro de bienes de inversión ----------
export const InvestmentQuerySchema = z.object({
    issuerId: z.string().min(1).optional(),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export const InvestmentRowSchema = z.object({
    id: z.string(),
    issuerId: z.string(),
    issuerName: z.string(),
    acquisitionDate: z.string(),
    supplierName: z.string(),
    invoiceNumber: z.string().nullable(),
    description: z.string().nullable(),
    category: ExpenseCategorySchema,
    baseCents: z.number().int(),
    usefulLifeYears: z.number().int(),
    deductiblePct: z.number().int(),
    /** Amortización acumulada al cierre del año anterior. */
    accumulatedBeforeCents: z.number().int(),
    /** Amortización del año consultado (contable, sin aplicar el % deducible). */
    amortizationYearCents: z.number().int(),
    /** La parte de esa amortización que es gasto deducible (con el % deducible). */
    deductibleAmortizationYearCents: z.number().int(),
    /** Amortización acumulada al cierre del año consultado. */
    accumulatedCents: z.number().int(),
    /** Valor pendiente de amortizar al cierre del año consultado. */
    netValueCents: z.number().int(),
});
export const InvestmentResponseSchema = z.object({
    year: z.number().int(),
    rows: z.array(InvestmentRowSchema),
    totals: z.object({
        baseCents: z.number().int(),
        amortizationYearCents: z.number().int(),
        deductibleAmortizationYearCents: z.number().int(),
        accumulatedCents: z.number().int(),
        netValueCents: z.number().int(),
    }),
});
// ---------- Resultado y borradores de los modelos 303 y 130 ----------
export const BooksSummaryQuerySchema = z.object({
    issuerId: z.string().min(1),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export const BooksQuarterSchema = z.object({
    quarter: z.number().int(),
    /** Facturas emitidas del trimestre (sin suplidos): base imponible. */
    incomeBaseCents: z.number().int(),
    invoiceCount: z.number().int(),
    /** Gasto deducible del trimestre (base × % deducible, sin bienes de inversión). */
    expensesCents: z.number().int(),
    expenseCount: z.number().int(),
    /** Amortización deducible devengada en el trimestre. */
    amortizationCents: z.number().int(),
    /** Ingresos − gastos − amortización. */
    netResultCents: z.number().int(),
});
export const Vat303QuarterSchema = z.object({
    quarter: z.number().int(),
    /** IVA devengado por tipo (de las facturas emitidas del trimestre). */
    accruedByRate: z.array(LedgerVatLineSchema),
    accruedBaseCents: z.number().int(),
    accruedVatCents: z.number().int(),
    /** Base de operaciones a tipo 0 (exentas o no sujetas), ya incluida arriba. */
    exemptBaseCents: z.number().int(),
    /** IVA soportado deducible en gastos corrientes. */
    deductibleCurrentBaseCents: z.number().int(),
    deductibleCurrentVatCents: z.number().int(),
    /** IVA soportado deducible en bienes de inversión. */
    deductibleInvestmentBaseCents: z.number().int(),
    deductibleInvestmentVatCents: z.number().int(),
    /** Devengado − deducible. Positivo: a ingresar; negativo: a compensar o devolver. */
    resultCents: z.number().int(),
});
export const Irpf130QuarterSchema = z.object({
    quarter: z.number().int(),
    /** Acumulado del año hasta el fin del trimestre. */
    ytdIncomeCents: z.number().int(),
    ytdExpensesCents: z.number().int(),
    ytdNetCents: z.number().int(),
    /** 20 % del rendimiento neto acumulado (0 si es negativo). */
    ratePaymentCents: z.number().int(),
    /** Retenciones soportadas acumuladas del año. */
    ytdRetentionsCents: z.number().int(),
    /** Pagos de los trimestres anteriores, calculados con este mismo criterio. */
    previousPaymentsCents: z.number().int(),
    /** A ingresar este trimestre (nunca negativo). */
    paymentCents: z.number().int(),
});
export const BooksSummaryResponseSchema = z.object({
    year: z.number().int(),
    issuer: z.object({ id: z.string(), name: z.string(), nif: z.string() }),
    quarters: z.array(BooksQuarterSchema),
    vat303: z.array(Vat303QuarterSchema),
    irpf130: z.array(Irpf130QuarterSchema),
    totals: z.object({
        incomeBaseCents: z.number().int(),
        expensesCents: z.number().int(),
        amortizationCents: z.number().int(),
        netResultCents: z.number().int(),
        accruedVatCents: z.number().int(),
        deductibleVatCents: z.number().int(),
        retentionsCents: z.number().int(),
    }),
    /**
     * Prueba del 70 %: si en el año anterior al menos el 70 % de los ingresos de la actividad
     * llevó retención, no hay obligación de presentar el modelo 130. `null` si no hubo ingresos.
     */
    retentionShare: z.object({
        previousYear: z.number().int(),
        retainedBaseCents: z.number().int(),
        totalBaseCents: z.number().int(),
        percent: z.number().nullable(),
        likelyExempt: z.boolean(),
    }),
});
//# sourceMappingURL=books.js.map